import React from 'react';
import { createPortal } from 'react-dom';
import { X, User, Mail, Phone, MapPin, Tag, MessageSquare, Search, ChevronDown, UserPlus, Building2 } from 'lucide-react';
import { InlineLoadingState } from '../../../components/LoadingSpinner';
import {
  Contact,
  CONTACT_TYPES_SELECTABLE_FOR_LEAD,
  ContactType,
  Lead,
  contactDisplayName,
  contactTypeClearsCompany,
  contactTypeRequiresCompany,
  contactTypeRequiresLastName,
  isContactSelectableForLeadInquiry,
} from '../../../types';
import { AppSelect } from '../../../components/AppSelect';
import { AddressAutocompleteInput } from '../../../components/AddressAutocompleteInput';
import { UI_Z_CLASS } from '../../../utils/uiZIndex';
import { leadCategorySwatchClassName } from '../../../utils/leadCategoryColors';
import { SiteContactPicker } from './SiteContactPicker';
import {
  fetchInboxSuggestLeadApplied,
  getCachedInboxSuggestLead,
  invalidateInboxSuggestLeadCache,
  type InboxCustomerMatch,
  type InboxSuggestLeadApplied,
  type LeadCategoryOption,
} from '../inboxSuggestLeadCache';
import { leadFromCreateApiResponse } from '../utils/leadFromCreateApiResponse';
import { CustomerBlockNotice } from './CustomerPartySummary';
import { useDiscardGuard, useUserEdited } from '../../../hooks/useDiscardGuard';

interface CreateLeadModalProps {
  onClose: () => void;
  onCreated: (lead: Lead) => void;
  /** Gesetzt beim Öffnen aus dem Postfach: Server lädt Thread und liefert KI-/Heuristik-Vorschläge (inbox_emails.id = UUID). */
  prefillAnchorEmailId?: string | null;
  /** Vorausgewählter Kontakt (z. B. aus der Detailseite eines Kontakts). */
  prefillContactId?: string | number | null;
  /** Vorausgewählte Organisation (z. B. aus Org-Detail oder ?createFor=organization:). */
  prefillOrganizationId?: string | null;
  /** z-index / Overlay-Klassen (z. B. über Plantafel-Drawer). Standard: z-50 */
  overlayClassName?: string;
  /**
   * `drawer` = rechte Seitenleiste wie Plantafel „Neue Aktivität“ (Standard, überall gleich wie in der Projektansicht).
   * `modal` = zentrierter Dialog.
   */
  variant?: 'modal' | 'drawer';
  /**
   * `offer` = Schnellstart „Neues Angebot“: Titel und Kategorie optional, ohne Beschreibung.
   * Der Server legt mit dem Projekt das erste Angebot an; `onCreated` erhält `primary_offering_id`.
   */
  intent?: 'project' | 'offer';
}

const EMPTY_LEAD_FORM = {
  contact_id: null as string | number | null,
  contact_type: 'Privatkunde' as ContactType,
  first_name: '',
  last_name: '',
  company: '',
  email: '',
  phone: '',
  address: '',
  site_address: '',
  category: '',
  lead_title: '',
  notes: '',
};

type ContactOption = {
  id: string | number;
  name: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  phone: string;
  address: string;
  company?: string | null;
  organization_id?: string | null;
  contact_kind?: string | null;
  is_primary?: boolean;
  is_blocked?: boolean;
  block_reason?: string;
  warning_text?: string;
};

type OrgOption = {
  id: string;
  name: string;
  is_blocked?: boolean;
  block_reason?: string;
  warning_text?: string;
};

type NewPersonPrefill = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  address: string;
};

type CustomerSelection =
  | { kind: 'none' }
  | { kind: 'new' }
  | { kind: 'private'; contactId: string | number }
  | { kind: 'organization'; organizationId: string };

function mapContactOption(c: Contact & { is_primary?: boolean }): ContactOption {
  return {
    id: c.id,
    name: c.name ?? '',
    first_name: c.first_name,
    last_name: c.last_name,
    email: c.email ?? '',
    phone: c.phone ?? '',
    address: c.address ?? '',
    company: c.company,
    organization_id: c.organization_id ?? null,
    contact_kind: c.contact_kind ?? null,
    is_primary: Boolean(c.is_primary),
    is_blocked: Boolean(c.is_blocked),
    block_reason: c.block_reason ?? '',
    warning_text: c.warning_text ?? '',
  };
}

function mapOrgOption(o: {
  id?: string;
  name?: string;
  is_blocked?: boolean;
  block_reason?: string;
  warning_text?: string;
}): OrgOption {
  return {
    id: String(o.id ?? ''),
    name: String(o.name ?? '').trim(),
    is_blocked: Boolean(o.is_blocked),
    block_reason: o.block_reason ?? '',
    warning_text: o.warning_text ?? '',
  };
}

/** Legacy commercial rows without an organization — still selectable for projects. */
function isLegacyCommercialContact(c: Contact): boolean {
  if (c.organization_id) return false;
  if (c.contact_kind === 'Privatkunde' || c.contact_type === 'Privatkunde') return false;
  return c.contact_type === 'Gewerblicher Kunde' || c.contact_kind === 'Gewerblicher Kontakt';
}

function matchesCustomerSearch(c: ContactOption, q: string): boolean {
  return (
    (c.name || '').toLowerCase().includes(q) ||
    (c.first_name || '').toLowerCase().includes(q) ||
    (c.last_name || '').toLowerCase().includes(q) ||
    (c.email || '').toLowerCase().includes(q) ||
    (c.phone || '').toLowerCase().includes(q) ||
    (c.address || '').toLowerCase().includes(q) ||
    (c.company || '').toLowerCase().includes(q)
  );
}

function applyContactToForm(
  prev: typeof EMPTY_LEAD_FORM,
  c: ContactOption,
  companyFallback?: string | null,
  options?: { syncSiteAddress?: boolean },
) {
  const address = (c.address ?? '').trim();
  return {
    ...prev,
    contact_id: c.id,
    first_name: (c.first_name ?? '').trim() || '',
    last_name: (c.last_name ?? '').trim() || '',
    company: (c.company ?? companyFallback ?? '').trim() || '',
    email: c.email || '',
    phone: c.phone || '',
    address: address || '',
    // On customer/person switch, replace site address (clear if contact has none)
    // so a previous Privatkunde address does not stick on org selection.
    ...(options?.syncSiteAddress ? { site_address: address || '' } : {}),
  };
}

export const CreateLeadModal: React.FC<CreateLeadModalProps> = ({
  onClose,
  onCreated,
  prefillAnchorEmailId,
  prefillContactId,
  prefillOrganizationId,
  overlayClassName = 'z-50',
  variant = 'drawer',
  intent = 'project',
}) => {
  const isDrawer = variant === 'drawer';
  const isOffer = intent === 'offer';
  const createFailedMessage = isOffer
    ? 'Angebot konnte nicht erstellt werden.'
    : 'Projekt konnte nicht angelegt werden.';
  const anchorKey = (prefillAnchorEmailId ?? '').trim();
  const initialOrgId = (prefillOrganizationId ?? '').trim() || null;
  const initialCachedPrefill = anchorKey ? getCachedInboxSuggestLead(anchorKey) : undefined;

  const [privateContacts, setPrivateContacts] = React.useState<ContactOption[]>([]);
  const [legacyCommercialContacts, setLegacyCommercialContacts] = React.useState<ContactOption[]>([]);
  const [allContacts, setAllContacts] = React.useState<ContactOption[]>([]);
  const [organizations, setOrganizations] = React.useState<OrgOption[]>([]);
  const [orgContacts, setOrgContacts] = React.useState<ContactOption[]>([]);
  const [customer, setCustomer] = React.useState<CustomerSelection>({ kind: 'none' });
  const [customerOpen, setCustomerOpen] = React.useState(false);
  const [customerSearch, setCustomerSearch] = React.useState('');
  const customerDropdownRef = React.useRef<HTMLDivElement>(null);
  const [personOpen, setPersonOpen] = React.useState(false);
  const [personSearch, setPersonSearch] = React.useState('');
  const personDropdownRef = React.useRef<HTMLDivElement>(null);
  const [newPersonChosen, setNewPersonChosen] = React.useState(false);
  const [newCustomerChosen, setNewCustomerChosen] = React.useState(() => initialCachedPrefill != null);
  const [formData, setFormData] = React.useState(() => {
    const patch = initialCachedPrefill?.formPatch;
    return {
      ...EMPTY_LEAD_FORM,
      ...(patch ?? {}),
      site_address: (patch?.address ?? '').trim() || EMPTY_LEAD_FORM.site_address,
      contact_id: null,
    };
  });
  const [isSaving, setIsSaving] = React.useState(false);
  const [contactError, setContactError] = React.useState('');
  const [categoryError, setCategoryError] = React.useState('');
  const [inquiryError, setInquiryError] = React.useState('');
  const [hasDifferentSiteContact, setHasDifferentSiteContact] = React.useState(false);
  const [siteContactDraft, setSiteContactDraft] = React.useState<Contact | null>(null);
  /** Wenn true, folgt die Baustellenadresse nicht mehr automatisch der Privatkunden-Adresse. */
  const [siteAddressTouched, setSiteAddressTouched] = React.useState(false);
  const [prefillLoading, setPrefillLoading] = React.useState(
    () => Boolean(anchorKey) && initialCachedPrefill == null,
  );
  const [prefillWarning, setPrefillWarning] = React.useState<string | null>(
    () => initialCachedPrefill?.prefillWarning ?? null,
  );
  const [prefillDetailNote, setPrefillDetailNote] = React.useState(
    () => initialCachedPrefill?.prefillDetailNote ?? '',
  );
  /** Hinweis, wenn der Vorschlag einem bestehenden Kunden zugeordnet wurde (oder die Firma schon existiert). */
  const [customerMatchNotice, setCustomerMatchNotice] = React.useState<string | null>(null);
  const [categories, setCategories] = React.useState<LeadCategoryOption[]>([]);
  const categoriesRef = React.useRef(categories);
  categoriesRef.current = categories;
  const didApplyPrefillRef = React.useRef(false);
  const selectOrgRequestIdRef = React.useRef(0);

  const selectedOrganizationId = customer.kind === 'organization' ? customer.organizationId : null;
  const selectedOrganization =
    selectedOrganizationId != null
      ? organizations.find((o) => o.id === selectedOrganizationId) ?? null
      : null;
  const orgMode = customer.kind === 'organization';
  const creatingNewCustomer = customer.kind === 'new' || newCustomerChosen;
  /** Vorname/E-Mail/… nur bei neuem Kunden bzw. neuem Ansprechpartner — nicht bei bestehendem Kontakt. */
  const editingNewContact = creatingNewCustomer || (orgMode && newPersonChosen);

  const selectOrganizationRef = React.useRef<
    (organizationId: string, preferredContactId?: string | null, newPerson?: NewPersonPrefill) => Promise<void>
  >(async () => {});

  const applyPrefill = React.useCallback((applied: InboxSuggestLeadApplied) => {
    const patch = applied.formPatch;
    setFormData((prev) => {
      const siteFromPrefill = (patch.address ?? '').trim();
      return {
        ...prev,
        ...patch,
        site_address: siteFromPrefill || prev.site_address,
        contact_id: null,
      };
    });
    setPrefillDetailNote(applied.prefillDetailNote);
    setNewPersonChosen(false);
    setOrgContacts([]);
    setSiteAddressTouched(false);
    setPrefillWarning(applied.prefillWarning);
    setCustomerMatchNotice(null);

    // Bestehenden Kunden vorauswählen statt immer „Neuer Kunde“ (sonst entstehen Dubletten).
    const match: InboxCustomerMatch | null = applied.customerMatch;
    if (match?.kind === 'organization') {
      const hasPerson = Boolean(patch.first_name.trim() || patch.last_name.trim());
      const newPerson = !match.contact_id && hasPerson;
      void selectOrganizationRef.current(
        match.organization_id,
        match.contact_id,
        newPerson
          ? {
              first_name: patch.first_name,
              last_name: patch.last_name,
              email: patch.email,
              phone: patch.phone,
              address: patch.address,
            }
          : undefined,
      );
      setCustomerMatchNotice(
        `Bestehender Kunde erkannt: ${match.organization_name} (${
          match.reason === 'email' ? 'über E-Mail-Adresse' : 'über Firmenname'
        })${newPerson ? ' – Ansprechpartner wird neu angelegt' : ''}.`,
      );
      return;
    }
    if (match?.kind === 'contact') {
      const c = match.contact;
      const option: ContactOption = {
        id: c.id,
        name: c.name ?? '',
        first_name: c.first_name,
        last_name: c.last_name,
        email: c.email ?? '',
        phone: c.phone ?? '',
        address: c.address ?? '',
        company: c.company,
        organization_id: null,
      };
      setCustomer({ kind: 'private', contactId: c.id });
      setNewCustomerChosen(false);
      setFormData((prev) => applyContactToForm(prev, option, null, { syncSiteAddress: !patch.address.trim() }));
      setCustomerMatchNotice(
        `Bestehender Kunde erkannt: ${(c.company ?? '').trim() || contactDisplayName(option)} (${
          match.reason === 'email' ? 'über E-Mail-Adresse' : 'über Name'
        }).`,
      );
      return;
    }
    setCustomer({ kind: 'new' });
    setNewCustomerChosen(true);
    if (match?.kind === 'company_hint') {
      setCustomerMatchNotice(
        `„${match.company}“ gibt es schon als Kontakt (${match.contact_labels.join(', ')}). Bitte prüfen, ob der Kunde bereits angelegt ist.`,
      );
    }
  }, []);

  React.useEffect(() => {
    fetch('/api/settings/lead-categories', { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => {
        if (!Array.isArray(data)) return;
        setCategories(
          data.map((row: { key?: string; id?: string; label?: string; color?: string }) => ({
            key: String(row.key ?? row.id ?? ''),
            label: String(row.label ?? ''),
            color: row.color != null ? String(row.color) : undefined,
          })).filter((c) => c.key && c.label),
        );
      })
      .catch(() => {});
  }, []);

  // Angebots-Schnellstart: gibt es genau eine Kategorie, ist sie vorausgewählt.
  React.useEffect(() => {
    if (!isOffer || categories.length !== 1) return;
    const onlyKey = categories[0].key;
    setFormData((prev) => (prev.category ? prev : { ...prev, category: onlyKey }));
  }, [isOffer, categories]);

  React.useEffect(() => {
    if (!anchorKey) return;

    const cached = getCachedInboxSuggestLead(anchorKey);
    if (cached) {
      applyPrefill(cached);
      setPrefillLoading(false);
      return;
    }

    let cancelled = false;
    setPrefillLoading(true);
    setPrefillWarning(null);

    const loadCategories = async (): Promise<LeadCategoryOption[]> => {
      const res = await fetch('/api/settings/lead-categories', { credentials: 'include' });
      if (!res.ok) return categoriesRef.current;
      const data = await res.json();
      if (!Array.isArray(data)) return categoriesRef.current;
      const loaded = data
        .map((row: { key?: string; id?: string; label?: string; color?: string }) => ({
          key: String(row.key ?? row.id ?? ''),
          label: String(row.label ?? ''),
          color: row.color != null ? String(row.color) : undefined,
        }))
        .filter((c) => c.key && c.label);
      if (!cancelled) setCategories(loaded);
      return loaded.length > 0 ? loaded : categoriesRef.current;
    };

    void fetchInboxSuggestLeadApplied(anchorKey, loadCategories)
      .then((applied) => {
        if (cancelled) return;
        applyPrefill(applied);
      })
      .catch((err) => {
        if (!cancelled) {
          const hint = err instanceof Error && err.message ? err.message : '';
          setPrefillWarning(
            hint
              ? `Vorschläge konnten nicht geladen werden: ${hint}`
              : 'Vorschläge aus dem E-Mail-Thread konnten nicht geladen werden.',
          );
        }
      })
      .finally(() => {
        if (!cancelled) setPrefillLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [anchorKey, applyPrefill]);

  // Load Privatkunden + Organisationen for Kundenauswahl.
  React.useEffect(() => {
    let cancelled = false;
    void Promise.all([
      fetch('/api/contacts').then((r) => (r.ok ? r.json() : [])),
      fetch('/api/organizations?type=Gewerblicher%20Kunde').then((r) => (r.ok ? r.json() : [])),
    ])
      .then(([contactList, orgList]) => {
        if (cancelled) return;
        const rawContacts = Array.isArray(contactList) ? (contactList as Contact[]) : [];
        const selectable = rawContacts.filter((c) => {
          if (c.contact_kind === 'Privatkunde' || c.contact_kind === 'Gewerblicher Kontakt') return true;
          return isContactSelectableForLeadInquiry(c.contact_type);
        });
        const mapped = selectable.map((c) => mapContactOption(c));
        setAllContacts(mapped);
        setPrivateContacts(
          selectable
            .filter((c) => {
              if (c.contact_kind === 'Privatkunde') return true;
              if (c.contact_kind === 'Gewerblicher Kontakt' || c.organization_id) return false;
              return c.contact_type === 'Privatkunde';
            })
            .map((c) => mapContactOption(c)),
        );
        setLegacyCommercialContacts(
          selectable.filter((c) => isLegacyCommercialContact(c)).map((c) => mapContactOption(c)),
        );
        const orgs = (Array.isArray(orgList) ? orgList : [])
          .map((o: Parameters<typeof mapOrgOption>[0]) => mapOrgOption(o))
          .filter((o: OrgOption) => o.id && o.name);
        setOrganizations(orgs);
      })
      .catch(() => {
        if (!cancelled) {
          setAllContacts([]);
          setPrivateContacts([]);
          setLegacyCommercialContacts([]);
          setOrganizations([]);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectOrganization = React.useCallback(
    async (organizationId: string, preferredContactId?: string | null, newPerson?: NewPersonPrefill) => {
      const requestId = ++selectOrgRequestIdRef.current;
      setContactError('');
      setCustomer({ kind: 'organization', organizationId });
      setNewCustomerChosen(false);
      setCustomerOpen(false);
      setCustomerSearch('');
      setPersonOpen(false);
      setPersonSearch('');
      setHasDifferentSiteContact(false);
      setSiteContactDraft(null);
      setSiteAddressTouched(false);

      let orgName = organizations.find((o) => o.id === organizationId)?.name ?? '';

      try {
        const [orgRes, contactsRes] = await Promise.all([
          orgName
            ? Promise.resolve(null)
            : fetch(`/api/organizations/${organizationId}`).then((r) => (r.ok ? r.json() : null)),
          fetch(`/api/organizations/${organizationId}/contacts`).then((r) => (r.ok ? r.json() : [])),
        ]);
        if (requestId !== selectOrgRequestIdRef.current) return;

        if (orgRes && typeof orgRes === 'object' && orgRes.name != null) {
          orgName = String(orgRes.name);
          setOrganizations((prev) => {
            const mapped = mapOrgOption(orgRes as Parameters<typeof mapOrgOption>[0]);
            return prev.some((o) => o.id === organizationId)
              ? prev.map((o) => (o.id === organizationId ? { ...o, ...mapped } : o))
              : [...prev, mapped].sort((a, b) => a.name.localeCompare(b.name, 'de'));
          });
        }

        const mapped = (Array.isArray(contactsRes) ? contactsRes : []).map(
          (c: Contact & { is_primary?: boolean }) => mapContactOption(c),
        );
        setOrgContacts(mapped);

        const idStr = preferredContactId != null && preferredContactId !== '' ? String(preferredContactId) : '';
        const preferred =
          (idStr ? mapped.find((c) => String(c.id) === idStr) : undefined) ??
          (newPerson ? undefined : (mapped.find((c) => c.is_primary) ?? mapped[0]));

        if (preferred) {
          setFormData((prev) =>
            applyContactToForm(prev, preferred, orgName, { syncSiteAddress: true }),
          );
          setNewPersonChosen(false);
        } else {
          setFormData((prev) => ({
            ...prev,
            contact_id: null,
            first_name: newPerson?.first_name ?? '',
            last_name: newPerson?.last_name ?? '',
            company: orgName,
            email: newPerson?.email ?? '',
            phone: newPerson?.phone ?? '',
            address: '',
            site_address: newPerson?.address ?? '',
          }));
          setNewPersonChosen(true);
        }
      } catch {
        if (requestId !== selectOrgRequestIdRef.current) return;
        setOrgContacts([]);
        setFormData((prev) => ({
          ...prev,
          contact_id: null,
          company: orgName,
          first_name: newPerson?.first_name ?? '',
          last_name: newPerson?.last_name ?? '',
          email: newPerson?.email ?? '',
          phone: newPerson?.phone ?? '',
          address: '',
          site_address: newPerson?.address ?? '',
        }));
        setNewPersonChosen(true);
      }
    },
    [organizations],
  );
  selectOrganizationRef.current = selectOrganization;

  // Prefill from props once lists are ready.
  React.useEffect(() => {
    if (didApplyPrefillRef.current) return;
    if (anchorKey) return;
    if (organizations.length === 0 && allContacts.length === 0 && !initialOrgId) return;

    if (initialOrgId) {
      didApplyPrefillRef.current = true;
      void selectOrganization(initialOrgId, prefillContactId != null ? String(prefillContactId) : null);
      return;
    }

    if (prefillContactId == null || prefillContactId === '') return;
    if (allContacts.length === 0) return;

    const match = allContacts.find((c) => String(c.id) === String(prefillContactId));
    if (!match) return;
    didApplyPrefillRef.current = true;

    if (match.organization_id) {
      void selectOrganization(String(match.organization_id), String(match.id));
      return;
    }

    setCustomer({ kind: 'private', contactId: match.id });
    setNewCustomerChosen(false);
    setNewPersonChosen(false);
    setOrgContacts([]);
    setFormData((prev) => applyContactToForm(prev, match, null, { syncSiteAddress: true }));
  }, [initialOrgId, prefillContactId, allContacts, organizations, selectOrganization, anchorKey]);

  React.useEffect(() => {
    if (!customerOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(e.target as Node)) {
        setCustomerOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [customerOpen]);

  React.useEffect(() => {
    if (!personOpen) return;
    const onOutside = (e: MouseEvent) => {
      if (personDropdownRef.current && !personDropdownRef.current.contains(e.target as Node)) {
        setPersonOpen(false);
      }
    };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, [personOpen]);

  const inboxPrefillActive = anchorKey !== '';
  const selectedPrivateContact =
    customer.kind === 'private'
      ? privateContacts.find((c) => String(c.id) === String(customer.contactId)) ??
        legacyCommercialContacts.find((c) => String(c.id) === String(customer.contactId)) ??
        allContacts.find((c) => String(c.id) === String(customer.contactId))
      : null;
  const selectedOrgContact =
    formData.contact_id != null
      ? orgContacts.find((c) => String(c.id) === String(formData.contact_id))
      : null;

  const showContactFields =
    customer.kind === 'private' ||
    customer.kind === 'organization' ||
    creatingNewCustomer;

  const customerDisplayLabel = creatingNewCustomer
    ? 'Neuen Kunden erstellen'
    : customer.kind === 'organization'
      ? selectedOrganization?.name || 'Gewerblicher Kunde'
      : selectedPrivateContact
        ? ((selectedPrivateContact.company || '').trim() || contactDisplayName(selectedPrivateContact))
        : 'Kunde auswählen';

  const personDisplayLabel = newPersonChosen
    ? 'Weiteren Ansprechpartner hinzufügen'
    : selectedOrgContact
      ? `${contactDisplayName(selectedOrgContact)}${selectedOrgContact.email ? ` · ${selectedOrgContact.email}` : ''}`.trim()
      : 'Ansprechpartner auswählen';

  /** Kundenname für den Standardtitel im Angebots-Schnellstart: Firma bzw. Name des Kontakts. */
  const offerCustomerName = (() => {
    if (orgMode) return (selectedOrganization?.name || formData.company).trim();
    if (creatingNewCustomer) {
      const company = contactTypeClearsCompany(formData.contact_type) ? '' : formData.company.trim();
      return company || contactDisplayName(formData);
    }
    if (selectedPrivateContact) {
      return (selectedPrivateContact.company || '').trim() || contactDisplayName(selectedPrivateContact);
    }
    return '';
  })();
  const offerDefaultTitle = offerCustomerName ? `Angebot ${offerCustomerName}` : 'Angebot';

  const filteredPrivateContacts = React.useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return privateContacts;
    return privateContacts.filter((c) => matchesCustomerSearch(c, q));
  }, [privateContacts, customerSearch]);

  const filteredLegacyCommercialContacts = React.useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return legacyCommercialContacts;
    return legacyCommercialContacts.filter((c) => matchesCustomerSearch(c, q));
  }, [legacyCommercialContacts, customerSearch]);

  const filteredOrganizations = React.useMemo(() => {
    const q = customerSearch.trim().toLowerCase();
    if (!q) return organizations;
    return organizations.filter((o) => o.name.toLowerCase().includes(q));
  }, [organizations, customerSearch]);

  const filteredOrgContacts = React.useMemo(() => {
    const q = personSearch.trim().toLowerCase();
    if (!q) return orgContacts;
    return orgContacts.filter(
      (c) =>
        (c.name || '').toLowerCase().includes(q) ||
        (c.first_name || '').toLowerCase().includes(q) ||
        (c.last_name || '').toLowerCase().includes(q) ||
        (c.email || '').toLowerCase().includes(q) ||
        (c.phone || '').toLowerCase().includes(q),
    );
  }, [orgContacts, personSearch]);

  const clearContactError = () => setContactError('');

  const syncCustomerAddress = React.useCallback(
    (nextAddress: string) => {
      setFormData((prev) => ({
        ...prev,
        address: nextAddress,
        site_address:
          !siteAddressTouched &&
          creatingNewCustomer &&
          !orgMode &&
          prev.contact_type === 'Privatkunde'
            ? nextAddress
            : prev.site_address,
      }));
    },
    [siteAddressTouched, creatingNewCustomer, orgMode],
  );

  const handleSelectPrivateCustomer = (c: ContactOption) => {
    selectOrgRequestIdRef.current += 1;
    setCustomerMatchNotice(null);
    setContactError('');
    setCustomer({ kind: 'private', contactId: c.id });
    setNewCustomerChosen(false);
    setNewPersonChosen(false);
    setOrgContacts([]);
    setFormData((prev) => applyContactToForm(prev, c, null, { syncSiteAddress: true }));
    setHasDifferentSiteContact(false);
    setSiteContactDraft(null);
    setSiteAddressTouched(false);
    setCustomerOpen(false);
    setCustomerSearch('');
  };

  const handleSelectNewCustomer = () => {
    selectOrgRequestIdRef.current += 1;
    setCustomerMatchNotice(null);
    setContactError('');
    setCustomer({ kind: 'new' });
    setNewCustomerChosen(true);
    setNewPersonChosen(false);
    setOrgContacts([]);
    setFormData((prev) => ({
      ...prev,
      contact_id: null,
      contact_type: 'Privatkunde',
      first_name: '',
      last_name: '',
      company: '',
      email: '',
      phone: '',
      address: '',
      site_address: '',
    }));
    setHasDifferentSiteContact(false);
    setSiteContactDraft(null);
    setSiteAddressTouched(false);
    setCustomerOpen(false);
    setCustomerSearch('');
  };

  const handleSelectOrgContact = (c: ContactOption) => {
    setContactError('');
    setFormData((prev) =>
      applyContactToForm(prev, c, selectedOrganization?.name ?? null, { syncSiteAddress: true }),
    );
    setNewPersonChosen(false);
    setHasDifferentSiteContact(false);
    setSiteContactDraft(null);
    setSiteAddressTouched(false);
    setPersonOpen(false);
    setPersonSearch('');
  };

  const handleSelectNewPerson = () => {
    setContactError('');
    setFormData((prev) => ({
      ...prev,
      contact_id: null,
      first_name: '',
      last_name: '',
      company: selectedOrganization?.name ?? '',
      email: '',
      phone: '',
      address: '',
      site_address: '',
    }));
    setNewPersonChosen(true);
    setHasDifferentSiteContact(false);
    setSiteContactDraft(null);
    setSiteAddressTouched(false);
    setPersonOpen(false);
    setPersonSearch('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Alle Pflichtfelder auf einmal prüfen, damit jedes fehlende Feld markiert wird —
    // nicht nur das erste. Im Angebots-Schnellstart sind Titel und Kategorie optional.
    const nextInquiryError =
      !isOffer && !formData.lead_title.trim()
        ? 'Bitte den Projekt-Titel angeben (z. B. Wärmepumpe).'
        : '';
    const nextCategoryError = !isOffer && !formData.category ? 'Bitte wählen Sie eine Kategorie.' : '';
    let nextContactError = '';
    if (customer.kind === 'none') {
      nextContactError = 'Bitte wählen Sie einen Kunden oder „Neuen Kunden erstellen“.';
    } else if (orgMode && !newPersonChosen && formData.contact_id == null) {
      nextContactError = 'Bitte wählen Sie einen Ansprechpartner oder fügen Sie einen weiteren hinzu.';
    } else if (orgMode && newPersonChosen && !formData.first_name.trim() && !formData.last_name.trim()) {
      nextContactError = 'Bitte Vor- und/oder Nachname des Ansprechpartners angeben.';
    } else if (
      creatingNewCustomer &&
      !orgMode &&
      contactTypeRequiresLastName(formData.contact_type) &&
      !formData.last_name.trim()
    ) {
      nextContactError = 'Bitte den Nachnamen angeben.';
    } else if (
      creatingNewCustomer &&
      !orgMode &&
      contactTypeRequiresCompany(formData.contact_type) &&
      !formData.company.trim()
    ) {
      nextContactError = 'Bitte den Unternehmensnamen angeben.';
    }
    setInquiryError(nextInquiryError);
    setCategoryError(nextCategoryError);
    setContactError(nextContactError);
    if (nextInquiryError || nextCategoryError || nextContactError) return;

    setIsSaving(true);
    try {
      let contactId = formData.contact_id;

      if (orgMode && newPersonChosen && selectedOrganizationId) {
        const contactRes = await fetch('/api/contacts', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            first_name: formData.first_name.trim() || null,
            last_name: formData.last_name.trim() || null,
            email: formData.email.trim() || null,
            phone: formData.phone.trim() || null,
            address: formData.site_address.trim() || null,
            contact_kind: 'Gewerblicher Kontakt',
            organization_id: selectedOrganizationId,
          }),
        });
        if (!contactRes.ok) {
          const err = (await contactRes.json().catch(() => null)) as { error?: string } | null;
          setContactError(err?.error || 'Ansprechpartner konnte nicht angelegt werden.');
          return;
        }
        const created = (await contactRes.json()) as { id?: string | number };
        if (created.id == null) {
          setContactError('Ansprechpartner konnte nicht angelegt werden.');
          return;
        }
        contactId = created.id;
      }

      const body: Record<string, unknown> = isOffer
        ? {
            category: formData.category || null,
            lead_title: formData.lead_title.trim() || offerDefaultTitle,
            site_address: formData.site_address.trim() || null,
          }
        : {
            category: formData.category,
            lead_title: formData.lead_title || null,
            notes: formData.notes,
            site_address: formData.site_address.trim() || null,
          };
      if (prefillDetailNote.trim()) {
        body.ai_detail_note = prefillDetailNote.trim();
      }
      if (editingNewContact) {
        body.first_name = formData.first_name.trim() || null;
        body.last_name = formData.last_name.trim() || null;
        body.company =
          creatingNewCustomer && !orgMode && contactTypeClearsCompany(formData.contact_type)
            ? ''
            : formData.company.trim() || (selectedOrganization?.name ?? '');
        body.email = formData.email;
        body.phone = formData.phone;
        if (creatingNewCustomer && !orgMode) {
          body.address = formData.address.trim() || null;
        }
      }
      if (creatingNewCustomer && !orgMode) {
        body.contact_type = formData.contact_type;
      }
      if (hasDifferentSiteContact && siteContactDraft?.id != null) {
        body.site_contact_id = String(siteContactDraft.id);
      }
      if (contactId != null) body.contact_id = contactId;

      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        const data = (await response.json()) as Record<string, unknown>;
        if (anchorKey) invalidateInboxSuggestLeadCache(anchorKey);
        onCreated(leadFromCreateApiResponse(data));
        onClose();
      } else {
        const err = (await response.json().catch(() => null)) as { error?: string } | null;
        setContactError(err?.error || createFailedMessage);
      }
    } catch (error) {
      console.error('Failed to create lead:', error);
      setContactError(createFailedMessage);
    } finally {
      setIsSaving(false);
    }
  };

  // Klick daneben, Esc, ✕: nach eigenen Eingaben erst nachfragen. Vorbelegung zählt nicht.
  const { edited, editProps } = useUserEdited();
  const discardGuard = useDiscardGuard({
    dirty: edited,
    onClose,
    disabled: isSaving,
    description: isOffer
      ? 'Das Angebot ist noch nicht erstellt. Deine Eingaben gehen verloren.'
      : 'Das Projekt ist noch nicht angelegt. Deine Eingaben gehen verloren.',
  });
  const requestClose = discardGuard.requestClose;

  React.useEffect(() => {
    if (!isDrawer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (customerOpen || personOpen) {
        setCustomerOpen(false);
        setPersonOpen(false);
        return;
      }
      if (
        document.querySelector('[data-app-select-list]') ||
        document.querySelector('[data-app-select-panel]') ||
        document.querySelector('[data-org-type-picker-panel]') ||
        document.querySelector('[data-address-autocomplete-list]')
      ) {
        return;
      }
      requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDrawer, requestClose, customerOpen, personOpen]);

  /** Plantafel-Drawer: sentence-case medium labels; Modal: uppercase micro-labels. */
  const labelClass = isDrawer
    ? 'mb-1 block text-sm font-medium text-app-text-muted'
    : 'text-[10px] font-bold text-slate-400 uppercase';
  const sectionLabelClass = isDrawer
    ? 'mb-1 block text-sm font-medium text-app-text-muted'
    : 'text-[10px] font-bold text-slate-400 uppercase tracking-wide';
  const drawerInputClass =
    'w-full rounded-md border border-app-border bg-app-surface px-3 py-2 text-sm outline-none focus:border-pipedrive-blue focus:ring-1 focus:ring-pipedrive-blue';

  const formBody = (
    <form id="create-lead-form" onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0" {...editProps}>
      <div
        className={`flex-1 overflow-y-auto overscroll-contain ${
          isDrawer ? 'app-scrollbar space-y-4 p-4' : 'space-y-6 p-4 md:p-6'
        }`}
      >
        {inboxPrefillActive && prefillLoading ? (
          <div
            className="flex flex-col items-center justify-center gap-4 py-16 md:py-20"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <InlineLoadingState label="Vorschläge aus dem E-Mail-Thread werden ermittelt …" />
          </div>
        ) : (
          <>
            {inboxPrefillActive && prefillWarning && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm">
                <p className="text-amber-800" role="alert">
                  {prefillWarning}
                </p>
              </div>
            )}
            <div className={`grid ${isDrawer ? 'grid-cols-1 gap-4' : 'grid-cols-2 gap-6'}`}>
              <div className={isDrawer ? 'block text-sm' : 'space-y-1'}>
                <label className={labelClass}>
                  {isOffer ? (
                    'Titel'
                  ) : isDrawer ? (
                    <>
                      Titel <span className="text-red-500">*</span>
                    </>
                  ) : (
                    <>
                      Projekt-Titel <span className="text-red-500">*</span>
                    </>
                  )}
                </label>
                {isDrawer ? (
                  <input
                    type="text"
                    value={formData.lead_title}
                    onChange={(e) => {
                      setInquiryError('');
                      setFormData((prev) => ({ ...prev, lead_title: e.target.value }));
                    }}
                    className={`${drawerInputClass} ${inquiryError ? 'border-red-400' : ''}`}
                    placeholder={isOffer ? offerDefaultTitle : 'z.B. Wärmepumpe, Heizkessel'}
                  />
                ) : (
                  <div className="relative">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={formData.lead_title}
                      onChange={(e) => {
                        setInquiryError('');
                        setFormData((prev) => ({ ...prev, lead_title: e.target.value }));
                      }}
                      className={`w-full pl-10 pr-4 py-2 bg-app-surface border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm ${
                        inquiryError ? 'border-red-400' : 'border-app-border'
                      }`}
                      placeholder={isOffer ? offerDefaultTitle : 'z.B. Wärmepumpe, Heizkessel'}
                    />
                  </div>
                )}
                {inquiryError && <p className="text-xs text-red-600 mt-1">{inquiryError}</p>}
              </div>

              <div className={isDrawer ? 'block text-sm' : 'space-y-1'}>
                <label className={labelClass}>
                  {isOffer ? (
                    'Kategorie'
                  ) : (
                    <>
                      Kategorie <span className="text-red-500">*</span>
                    </>
                  )}
                </label>
                <AppSelect
                  value={formData.category}
                  onChange={(v) => { setCategoryError(''); setFormData((prev) => ({ ...prev, category: v })); }}
                  options={[
                    { value: '', label: isOffer ? 'Ohne Kategorie' : 'Kategorie auswählen' },
                    ...categories.map((c) => ({
                      value: c.key,
                      label: c.label,
                      icon: (
                        <span
                          className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${leadCategorySwatchClassName(c.color)}`}
                          aria-hidden
                        />
                      ),
                    })),
                  ]}
                  className={`w-full ${categoryError ? '[&_button]:border-red-400' : ''}`}
                  triggerClassName={isDrawer ? '!min-h-0 h-auto !py-2 rounded-md' : undefined}
                />
                {categoryError && <p className="text-xs text-red-600 mt-1">{categoryError}</p>}
              </div>

              {/* Kundenauswahl */}
              <div className={`col-span-full ${isDrawer ? 'block text-sm' : 'space-y-1'}`} ref={customerDropdownRef}>
                <label className={labelClass}>
                  Kundenauswahl <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => { clearContactError(); setCustomerOpen((o) => !o); setPersonOpen(false); }}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm text-app-text bg-app-surface border outline-none ${
                      isDrawer
                        ? 'rounded-md focus:border-pipedrive-blue focus:ring-1 focus:ring-pipedrive-blue'
                        : 'rounded-lg focus:ring-2 focus:ring-pipedrive-blue/20 focus:border-pipedrive-blue'
                    } ${
                      contactError && customer.kind === 'none' ? 'border-red-400' : 'border-app-border'
                    }`}
                  >
                    <span className="truncate flex items-center gap-2 min-w-0">
                      {orgMode && <Building2 className="w-4 h-4 shrink-0 text-slate-400" />}
                      <span className="truncate">{customerDisplayLabel}</span>
                    </span>
                    <ChevronDown className="w-4 h-4 shrink-0 text-app-text-subtle" />
                  </button>
                  {customerOpen && (
                    <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-app-surface border border-app-border rounded-lg shadow-lg overflow-hidden flex flex-col max-h-72">
                      <div className="p-2 border-b border-app-border">
                        <div className="relative">
                          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="text"
                            value={customerSearch}
                            onChange={(e) => setCustomerSearch(e.target.value)}
                            placeholder="Suchen..."
                            className="w-full pl-8 pr-3 py-1.5 text-sm border border-app-border rounded-md focus:ring-1 focus:ring-pipedrive-blue outline-none"
                          />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={handleSelectNewCustomer}
                        className={`w-full shrink-0 text-left px-3 py-2.5 text-sm font-medium border-b border-app-border flex items-center gap-2 transition-colors ${
                          creatingNewCustomer
                            ? 'bg-pipedrive-blue text-white'
                            : 'text-pipedrive-blue hover:bg-sky-50'
                        }`}
                      >
                        <UserPlus className="w-4 h-4 shrink-0" />
                        Neuen Kunden erstellen
                      </button>
                      <div className="overflow-y-auto flex-1 min-h-0">
                        {(filteredOrganizations.length > 0 || filteredLegacyCommercialContacts.length > 0) && (
                          <>
                            <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                              Gewerbliche Kunden
                            </p>
                            {filteredOrganizations.map((o) => (
                              <button
                                key={o.id}
                                type="button"
                                onClick={() => {
                                  setCustomerMatchNotice(null);
                                  void selectOrganization(o.id);
                                }}
                                className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                                  selectedOrganizationId === o.id
                                    ? 'bg-pipedrive-blue text-white'
                                    : 'text-app-text hover:bg-app-hover'
                                }`}
                              >
                                <Building2 className="w-3.5 h-3.5 shrink-0 opacity-70" />
                                <span className="truncate">{o.name}</span>
                              </button>
                            ))}
                            {filteredLegacyCommercialContacts.map((c) => (
                              <button
                                key={`legacy-${String(c.id)}`}
                                type="button"
                                onClick={() => handleSelectPrivateCustomer(c)}
                                className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center gap-2 ${
                                  customer.kind === 'private' && String(customer.contactId) === String(c.id)
                                    ? 'bg-pipedrive-blue text-white'
                                    : 'text-app-text hover:bg-app-hover'
                                }`}
                              >
                                <Building2 className="w-3.5 h-3.5 shrink-0 opacity-70" />
                                <span className="truncate">
                                  {(c.company || '').trim() || contactDisplayName(c)}
                                  {c.email ? ` · ${c.email}` : ''}
                                </span>
                              </button>
                            ))}
                          </>
                        )}
                        {filteredPrivateContacts.length > 0 && (
                          <>
                            <p className="px-3 pt-2 pb-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                              Privatkunden
                            </p>
                            {filteredPrivateContacts.map((c) => (
                              <button
                                key={String(c.id)}
                                type="button"
                                onClick={() => handleSelectPrivateCustomer(c)}
                                className={`w-full text-left px-3 py-2 text-sm transition-colors ${
                                  customer.kind === 'private' && String(customer.contactId) === String(c.id)
                                    ? 'bg-pipedrive-blue text-white'
                                    : 'text-app-text hover:bg-app-hover'
                                }`}
                              >
                                {contactDisplayName(c)}
                                {c.email ? ` · ${c.email}` : ''}
                              </button>
                            ))}
                          </>
                        )}
                        {filteredOrganizations.length === 0 &&
                          filteredLegacyCommercialContacts.length === 0 &&
                          filteredPrivateContacts.length === 0 && (
                          <p className="px-3 py-2 text-sm text-app-text-subtle">Keine Kunden gefunden.</p>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                {(() => {
                  const selected =
                    customer.kind === 'organization'
                      ? organizations.find((o) => o.id === customer.organizationId)
                      : customer.kind === 'private'
                        ? privateContacts.find((c) => String(c.id) === String(customer.contactId)) ??
                          legacyCommercialContacts.find((c) => String(c.id) === String(customer.contactId))
                        : null;
                  if (!selected) return null;
                  return (
                    <CustomerBlockNotice
                      className="mt-2"
                      isBlocked={selected.is_blocked}
                      blockReason={selected.block_reason}
                      warningText={selected.warning_text}
                    />
                  );
                })()}
                {inboxPrefillActive && customerMatchNotice && (
                  <p className="mt-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                    {customerMatchNotice}
                  </p>
                )}
              </div>

              {/* Ansprechpartner – nur bei gewerblichem Kunden */}
              {orgMode && (
                <div className="col-span-full space-y-1" ref={personDropdownRef}>
                  <label className={labelClass}>
                    Ansprechpartner <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => { clearContactError(); setPersonOpen((o) => !o); setCustomerOpen(false); }}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm text-app-text bg-app-surface border outline-none ${
                        isDrawer
                          ? 'rounded-md focus:border-pipedrive-blue focus:ring-1 focus:ring-pipedrive-blue'
                          : 'rounded-lg focus:ring-2 focus:ring-pipedrive-blue/20 focus:border-pipedrive-blue'
                      } ${
                        contactError ? 'border-red-400' : 'border-app-border'
                      }`}
                    >
                      <span className="truncate flex items-center gap-2 min-w-0">
                        <span className="truncate">{personDisplayLabel}</span>
                        {!newPersonChosen && selectedOrgContact?.is_primary && (
                          <span className="shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-app-surface-muted text-app-text-muted">
                            Haupt
                          </span>
                        )}
                      </span>
                      <ChevronDown className="w-4 h-4 shrink-0 text-app-text-subtle" />
                    </button>
                    {personOpen && (
                      <div className="absolute top-full left-0 right-0 z-50 mt-1 bg-app-surface border border-app-border rounded-lg shadow-lg overflow-hidden flex flex-col max-h-64">
                        <div className="p-2 border-b border-app-border">
                          <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input
                              type="text"
                              value={personSearch}
                              onChange={(e) => setPersonSearch(e.target.value)}
                              placeholder="Suchen..."
                              className="w-full pl-8 pr-3 py-1.5 text-sm border border-app-border rounded-md focus:ring-1 focus:ring-pipedrive-blue outline-none"
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={handleSelectNewPerson}
                          className={`w-full shrink-0 text-left px-3 py-2.5 text-sm font-medium border-b border-app-border flex items-center gap-2 transition-colors ${
                            newPersonChosen
                              ? 'bg-pipedrive-blue text-white'
                              : 'text-pipedrive-blue hover:bg-sky-50'
                          }`}
                        >
                          <UserPlus className="w-4 h-4 shrink-0" />
                          Weiteren Ansprechpartner hinzufügen
                        </button>
                        <div className="overflow-y-auto flex-1 min-h-0">
                          {filteredOrgContacts.map((c) => (
                            <button
                              key={String(c.id)}
                              type="button"
                              onClick={() => handleSelectOrgContact(c)}
                              className={`w-full text-left px-3 py-2 text-sm transition-colors flex items-center justify-between gap-2 ${
                                !newPersonChosen && String(formData.contact_id) === String(c.id)
                                  ? 'bg-pipedrive-blue text-white'
                                  : 'text-app-text hover:bg-app-hover'
                              }`}
                            >
                              <span className="truncate">
                                {contactDisplayName(c)}
                                {c.email ? ` · ${c.email}` : ''}
                              </span>
                              {c.is_primary && (
                                <span
                                  className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                                    !newPersonChosen && String(formData.contact_id) === String(c.id)
                                      ? 'bg-white/20 text-white'
                                      : 'bg-app-surface-muted text-app-text-muted'
                                  }`}
                                >
                                  Haupt
                                </span>
                              )}
                            </button>
                          ))}
                          {filteredOrgContacts.length === 0 && (
                            <p className="px-3 py-2 text-sm text-app-text-subtle">Keine Ansprechpartner vorhanden.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {contactError && (
                <p className="col-span-full text-xs text-red-600 -mt-4">{contactError}</p>
              )}

              {showContactFields && (
                <>
                  {creatingNewCustomer && !orgMode && (
                    <div className="col-span-full flex flex-col gap-2">
                      <label className={labelClass}>
                        Kontaktart <span className="text-red-500">*</span>
                      </label>
                      <div
                        className="flex w-full flex-wrap rounded-lg border border-app-border bg-app-surface-muted/80 p-0.5 gap-0.5"
                        role="group"
                        aria-label="Kontaktart"
                      >
                        {CONTACT_TYPES_SELECTABLE_FOR_LEAD.map((t) => (
                          <button
                            key={t}
                            type="button"
                            onClick={() => {
                              setContactError('');
                              setFormData((prev) => {
                                const nextType = t;
                                const next = {
                                  ...prev,
                                  contact_type: nextType,
                                  company: contactTypeClearsCompany(nextType) ? '' : prev.company,
                                };
                                if (
                                  !siteAddressTouched &&
                                  nextType === 'Privatkunde' &&
                                  prev.address.trim()
                                ) {
                                  next.site_address = prev.address;
                                }
                                return next;
                              });
                            }}
                            className={`flex-1 min-w-[7.5rem] px-2 py-2 text-[11px] font-medium rounded-md transition-all leading-tight ${
                              formData.contact_type === t
                                ? 'bg-app-surface text-app-text shadow-sm ring-1 ring-slate-200/80'
                                : 'text-app-text-muted hover:text-app-text hover:bg-app-hover/80'
                            }`}
                          >
                            {t}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Neuer Ansprechpartner bei bestehender Organisation */}
                  {orgMode && newPersonChosen && (
                    <>
                      <div className="space-y-1 col-span-full sm:col-span-1">
                        <label className={labelClass}>Vorname</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="text"
                            value={formData.first_name}
                            onChange={(e) => setFormData((prev) => ({ ...prev, first_name: e.target.value }))}
                            className="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                            placeholder="z.B. Max"
                          />
                        </div>
                      </div>
                      <div className="space-y-1 col-span-full sm:col-span-1">
                        <label className={labelClass}>Nachname</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="text"
                            value={formData.last_name}
                            onChange={(e) => setFormData((prev) => ({ ...prev, last_name: e.target.value }))}
                            className="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                            placeholder="z.B. Mustermann"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {creatingNewCustomer && !orgMode && formData.contact_type === 'Privatkunde' && (
                    <>
                      <div className="space-y-1 col-span-full sm:col-span-1">
                        <label className={labelClass}>Vorname</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="text"
                            value={formData.first_name}
                            onChange={(e) => setFormData((prev) => ({ ...prev, first_name: e.target.value }))}
                            className="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                            placeholder="z.B. Max"
                          />
                        </div>
                      </div>
                      <div className="space-y-1 col-span-full sm:col-span-1">
                        <label className={labelClass}>
                          Nachname <span className="text-red-500">*</span>
                        </label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="text"
                            value={formData.last_name}
                            onChange={(e) => setFormData((prev) => ({ ...prev, last_name: e.target.value }))}
                            className="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                            placeholder="z.B. Mustermann"
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {creatingNewCustomer && !orgMode && formData.contact_type === 'Gewerblicher Kunde' && (
                    <>
                      <div className="col-span-full space-y-3 rounded-lg border border-app-border bg-app-surface-muted/70 p-3">
                        <p className={sectionLabelClass}>Unternehmen</p>
                        <div className="space-y-3">
                          <div className="space-y-1">
                            <label className={labelClass}>
                              Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={formData.company}
                              onChange={(e) => setFormData((prev) => ({ ...prev, company: e.target.value }))}
                              className="w-full px-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                              placeholder="z.B. Muster GmbH"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className={labelClass} htmlFor="create-lead-company-address">
                              Firmensitz
                            </label>
                            <div className="relative">
                              <MapPin className="absolute left-3 top-[14px] z-[1] w-4 h-4 text-slate-400 pointer-events-none" />
                              <AddressAutocompleteInput
                                id="create-lead-company-address"
                                value={formData.address}
                                onChange={(v) => setFormData((prev) => ({ ...prev, address: v }))}
                                placeholder="Straße, Hausnummer, PLZ Ort"
                                inputClassName="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="col-span-full space-y-3 rounded-lg border border-app-border bg-app-surface p-3">
                        <p className={sectionLabelClass}>Ansprechpartner</p>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                          <div className="space-y-1">
                            <label className={labelClass}>Vorname</label>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <input
                                type="text"
                                value={formData.first_name}
                                onChange={(e) => setFormData((prev) => ({ ...prev, first_name: e.target.value }))}
                                className="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                                placeholder="z.B. Max"
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className={labelClass}>Nachname</label>
                            <div className="relative">
                              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <input
                                type="text"
                                value={formData.last_name}
                                onChange={(e) => setFormData((prev) => ({ ...prev, last_name: e.target.value }))}
                                className="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                                placeholder="z.B. Mustermann"
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className={labelClass}>E-Mail</label>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <input
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                                className="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                                placeholder="kunde@beispiel.de"
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <label className={labelClass}>Telefon</label>
                            <div className="relative">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                              <input
                                type="tel"
                                value={formData.phone}
                                onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                                className="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                                placeholder="+49 123 456789"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  {editingNewContact &&
                  !(creatingNewCustomer && !orgMode && formData.contact_type === 'Gewerblicher Kunde') ? (
                    <>
                      <div className="space-y-1 col-span-full sm:col-span-1">
                        <label className={labelClass}>E-Mail</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                            className="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                            placeholder="kunde@beispiel.de"
                          />
                        </div>
                      </div>
                      <div className="space-y-1 col-span-full sm:col-span-1">
                        <label className={labelClass}>Telefon</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <input
                            type="tel"
                            value={formData.phone}
                            onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                            className="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                            placeholder="+49 123 456789"
                          />
                        </div>
                      </div>
                    </>
                  ) : null}

                  {creatingNewCustomer && !orgMode && formData.contact_type === 'Privatkunde' ? (
                    <div className="space-y-1 col-span-full">
                      <label className={labelClass} htmlFor="create-lead-contact-address">
                        Adresse
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-3 top-[14px] z-[1] w-4 h-4 text-slate-400 pointer-events-none" />
                        <AddressAutocompleteInput
                          id="create-lead-contact-address"
                          value={formData.address}
                          onChange={syncCustomerAddress}
                          placeholder="Straße, Hausnummer, PLZ Ort"
                          inputClassName="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                        />
                      </div>
                    </div>
                  ) : null}

                  <div className="space-y-1 col-span-full">
                    <label className={labelClass} htmlFor="create-lead-site-address">
                      Baustellenadresse
                    </label>
                    <p className="text-[11px] text-app-text-subtle leading-snug">
                      {creatingNewCustomer && !orgMode && formData.contact_type === 'Privatkunde'
                        ? 'Wird automatisch mit der Kundenadresse befüllt — bei Bedarf abweichend anpassbar.'
                        : 'Adresse des Objekts. Die Kontaktadresse des Kunden bleibt unverändert.'}
                    </p>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-[14px] z-[1] w-4 h-4 text-slate-400 pointer-events-none" />
                      <AddressAutocompleteInput
                        id="create-lead-site-address"
                        value={formData.site_address}
                        onChange={(v) => {
                          setSiteAddressTouched(true);
                          setFormData((prev) => ({ ...prev, site_address: v }));
                        }}
                        placeholder="Straße, Hausnummer, PLZ Ort"
                        inputClassName="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm"
                      />
                    </div>
                  </div>
                </>
              )}
            </div>

            <div className="space-y-2">
              <label className="inline-flex items-center gap-2 text-sm text-app-text-muted">
                <input
                  type="checkbox"
                  className="rounded border-app-border-strong"
                  checked={hasDifferentSiteContact}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setHasDifferentSiteContact(checked);
                    if (!checked) setSiteContactDraft(null);
                  }}
                />
                Abweichender Baustellenkontakt
              </label>
              {hasDifferentSiteContact && (
                <div className="space-y-1">
                  <label className={labelClass}>Baustellenkontakt</label>
                  <p className="text-[11px] text-app-text-subtle leading-snug">
                    Ansprechpartner für den Monteur vor Ort. Rechnung bleibt beim Projektkontakt.
                  </p>
                  <SiteContactPicker
                    value={siteContactDraft}
                    disabled={isSaving}
                    onSelect={(c) => setSiteContactDraft(c)}
                  />
                </div>
              )}
            </div>

            {!isOffer && (
              <div className={isDrawer ? 'block text-sm' : 'space-y-1'}>
                <label className={labelClass}>{isDrawer ? 'Beschreibung' : 'Projektbeschreibung (Baustelle)'}</label>
                {isDrawer ? (
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                    rows={4}
                    className="min-h-[80px] w-full rounded-md border border-app-border px-3 py-2 text-sm outline-none focus:border-pipedrive-blue focus:ring-1 focus:ring-pipedrive-blue resize-none"
                    placeholder="z. B. Wünsche zum Objekt, technische Details…"
                  />
                ) : (
                  <div className="relative">
                    <MessageSquare className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
                    <textarea
                      value={formData.notes}
                      onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                      rows={4}
                      className="w-full pl-10 pr-4 py-2 bg-app-surface border border-app-border rounded focus:ring-1 focus:ring-pipedrive-blue outline-none transition-all text-sm resize-none"
                      placeholder="z. B. Wünsche zum Objekt, technische Details…"
                    />
                  </div>
                )}
              </div>
            )}
              </>
            )}
          </div>

          <div
            className={`shrink-0 border-t border-app-border ${
              isDrawer ? 'flex flex-wrap gap-2 p-3' : 'hidden md:flex justify-end gap-3 bg-app-surface-muted/50 px-6 py-4 border-app-border'
            }`}
          >
            {!isDrawer ? (
              <button
                type="button"
                onClick={onClose}
                className="btn-pipedrive-secondary"
              >
                Abbrechen
              </button>
            ) : null}
            <button
              type="submit"
              disabled={isSaving || prefillLoading}
              className={isDrawer ? 'btn-pipedrive-green flex-1 !py-2' : 'btn-pipedrive-green'}
            >
              {isSaving ? 'Wird erstellt...' : isOffer ? 'Angebot erstellen' : isDrawer ? 'Anlegen' : 'Projekt erstellen'}
            </button>
          </div>
        </form>
  );

  if (isDrawer) {
    return createPortal(
      <>
        <div
          className={`fixed inset-0 ${overlayClassName === 'z-50' ? UI_Z_CLASS.plantafelDrawer : overlayClassName} flex justify-end bg-black/25`}
          role="presentation"
          {...discardGuard.backdropProps}
        >
          <div
            className="relative flex h-full w-[min(400px,100%)] flex-col border-l border-app-border bg-app-surface shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="create-lead-drawer-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between border-b border-app-border p-4">
              <div className="min-w-0 flex-1 pr-2">
                <h3 id="create-lead-drawer-title" className="truncate text-lg font-semibold text-app-text">
                  {isOffer ? 'Angebot erstellen' : 'Neues Projekt anlegen'}
                </h3>
              </div>
              <button type="button" className="rounded p-1 text-app-text-subtle hover:bg-app-hover" onClick={requestClose}>
                <X className="h-4 w-4" />
              </button>
            </div>
            {formBody}
          </div>
        </div>
        {discardGuard.dialog}
      </>,
      document.body,
    );
  }

  return (
    <>
      <div
        className={`fixed inset-0 flex flex-col bg-app-surface md:items-center md:justify-center md:bg-black/40 md:backdrop-blur-[2px] md:p-4 ${overlayClassName}`}
        {...discardGuard.backdropProps}
      >
        <div className="flex flex-col w-full h-full md:h-auto md:bg-app-surface md:rounded-md md:max-w-2xl md:shadow-2xl md:overflow-hidden md:max-h-[90vh]" onClick={(e) => e.stopPropagation()}>
          <div className="md:hidden shrink-0 px-4 py-3 border-b border-app-border flex items-center justify-between bg-app-surface">
            <button onClick={requestClose} className="p-1.5 -ml-1.5 rounded text-app-text-subtle hover:bg-app-hover">
              <X className="w-5 h-5" />
            </button>
            <h2 className="text-base font-semibold text-app-text">{isOffer ? 'Angebot erstellen' : 'Neues Projekt'}</h2>
            <button
              type="submit"
              form="create-lead-form"
              disabled={isSaving || prefillLoading}
              className="btn-pipedrive-green text-sm disabled:opacity-50"
            >
              {isSaving ? 'Lädt…' : 'Erstellen'}
            </button>
          </div>

          <div className="hidden md:flex px-6 py-4 border-b border-app-border justify-between items-center bg-app-surface-muted/50">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded bg-pipedrive-green flex items-center justify-center text-white font-bold">
                +
              </div>
              <h2 className="text-base font-bold text-app-text leading-tight">
                {isOffer ? 'Angebot erstellen' : 'Neues Projekt anlegen'}
              </h2>
            </div>
            <button onClick={requestClose} className="p-1.5 hover:bg-slate-200 rounded transition-colors">
              <X className="w-5 h-5 text-app-text-subtle" />
            </button>
          </div>

          {formBody}
        </div>
      </div>
      {discardGuard.dialog}
    </>
  );
};
